/**
 * MasterFormCards — Onboarding Form (Master-Form) KPI cards for the CX Leader
 * Insights tab. Clicking a card opens CX Leader Tasks → Master Forms section on
 * the matching tab, carrying the active date range (card == tab == dataset).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, UserPlus, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { rangeParams } from '../../components/DateRangeFilter';

const authHeader = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ label, value, Icon, accent, testId, onClick, hover }) => (
  <button type="button" onClick={onClick} title={hover}
    className="text-left bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm hover:shadow-md hover:border-rose-300 transition w-full group relative"
    data-testid={testId}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent.bg}`}><Icon size={18} className={accent.fg} /></div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-medium text-gray-500 truncate">{label}</p>
      <p className="text-xl font-bold text-gray-900 mt-0.5" data-testid={`${testId}-value`}>{value}</p>
    </div>
    {hover && <span className="absolute hidden group-hover:block bottom-full left-3 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded whitespace-pre z-10">{hover}</span>}
  </button>
);

const MasterFormCards = ({ range = 'all', customFrom = '', customTo = '' }) => {
  const navigate = useNavigate();
  const [c, setC] = useState({});

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams(rangeParams(range, customFrom, customTo)).toString();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding-form/insights?${qs}`, { headers: authHeader() });
      const d = await res.json();
      setC(d.cards || {});
    } catch (e) { /* noop */ }
  }, [range, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const go = (tab) => {
    const p = new URLSearchParams({ section: 'master-forms', tab, ...rangeParams(range, customFrom, customTo) });
    navigate(`/tasks?${p.toString()}`);
  };

  return (
    <div data-testid="cxl-master-form-cards">
      <h3 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-2"><ClipboardList size={15} className="text-rose-600" /> Master Forms</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card label="Assigned Master Forms" value={c.assigned ?? 0} Icon={ClipboardList} accent={{ bg: 'bg-sky-50', fg: 'text-sky-600' }} testId="cxl-of-card-assigned" onClick={() => go('assigned')} />
        <Card label="Unassigned Master Forms" value={c.unassigned ?? 0} Icon={UserPlus} accent={{ bg: 'bg-gray-100', fg: 'text-gray-600' }} testId="cxl-of-card-unassigned" onClick={() => go('unassigned')} />
        <Card label="Completed Master Forms" value={c.completed ?? 0} Icon={CheckCircle2} accent={{ bg: 'bg-emerald-50', fg: 'text-emerald-600' }} testId="cxl-of-card-completed" onClick={() => go('completed')} />
        <Card label="Pending Master Forms" value={c.pending ?? 0} Icon={Clock} accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }} testId="cxl-of-card-pending" onClick={() => go('pending')} />
        <Card label="Overdue Master Forms" value={c.overdue ?? 0} Icon={AlertTriangle} accent={{ bg: 'bg-red-50', fg: 'text-red-600' }} testId="cxl-of-card-overdue" onClick={() => go('overdue')}
          hover={`Late: ${c.overdue_late ?? 0}\nCritical: ${c.overdue_critical ?? 0}`} />
      </div>
    </div>
  );
};

export default MasterFormCards;
